/**
 * List of sensitive parameter names that should never be logged.
 * This is the single source of truth for sensitive parameters across the application.
 */
export const SENSITIVE_PARAMS = [
  'token',
  'access_token',
  'accessToken',
  'refresh_token',
  'refreshToken',
  'code',
  'password',
  'passwd',
  'pwd',
  'secret',
  'apikey',
  'api_key',
  'api-key',
  'key',
  'authorization',
  'auth',
  'email',
  'partitionKey',
  'rowKey',
  'companyCode',
  'clientSecret',
  'client_secret',
  'privateKey',
  'private_key',
  'salt',
  'signature',
  'sig'
];

/**
 * Sanitize the params object by replacing sensitive values with 'REDACTED'.
 * This prevents sensitive data from being logged in telemetry.
 */
export function sanitizeParams(params: any): any {
  if (!params || typeof params !== 'object') return params;

  const sanitized = { ...params };
  SENSITIVE_PARAMS.forEach((key) => {
    if (key in sanitized) {
      sanitized[key] = 'REDACTED';
    }
  });

  return sanitized;
}

/**
 * Removes sensitive query parameters (e.g. token, code, password) from URLs.
 * This ensures that sensitive data is not logged in telemetry or error tracking.
 * Handles both encoded and decoded URLs properly.
 */
export function sanitizeUrl(url: string): string {
  if (!url) return url;

  try {
    // Check if URL is already encoded by looking for % characters
    const isEncoded = url.includes('%');

    if (isEncoded) {
      // For already encoded URLs, use regex to avoid double-encoding
      const paramsPattern = SENSITIVE_PARAMS.join('|');
      const regex = new RegExp(`(${paramsPattern})=([^&]*)`, 'gi');

      // Replace with REDACTED (not [REDACTED] to avoid encoding issues)
      return url.replace(regex, '$1=REDACTED');
    }

    // For non-encoded URLs, use URL API for proper parsing
    const parsedUrl = new URL(url, window.location.origin);

    // Redact sensitive parameters
    for (const param of SENSITIVE_PARAMS) {
      if (parsedUrl.searchParams.has(param)) {
        parsedUrl.searchParams.set(param, 'REDACTED');
      }
    }

    return parsedUrl.toString();
  } catch {
    // Fallback: Use regex to strip sensitive parameters
    const sensitiveParamsPattern = SENSITIVE_PARAMS.join('|');

    return url.replace(new RegExp(`(${sensitiveParamsPattern})=([^&]+)`, 'gi'), '$1=REDACTED');
  }
}
